import { useMemo, useState } from "react";
import type { DeploymentSummary } from "@/api/endpoints";

interface Props {
  deployments: DeploymentSummary[];
  value: string; // Tempo deployment id, "" = none
  onChange: (id: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

const MAX_RESULTS = 50;

// Searchable deployment picker. Matches on name or Tempo deployment ID, so a
// pasted ID (full or partial) finds the deployment directly.
export function DeploymentPicker({ deployments, value, onChange, disabled, placeholder }: Props) {
  const [query, setQuery] = useState("");
  const selected = deployments.find((d) => d.id === value) ?? null;

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const sorted = [...deployments].sort(
      (a, b) => Number(a.is_archived) - Number(b.is_archived) || a.name.localeCompare(b.name),
    );
    if (!q) return sorted;
    return sorted.filter((d) => d.name.toLowerCase().includes(q) || d.id.toLowerCase().includes(q));
  }, [deployments, query]);

  const onQuery = (text: string) => {
    setQuery(text);
    // A pasted full ID selects its deployment immediately.
    const exact = deployments.find((d) => d.id.toLowerCase() === text.trim().toLowerCase());
    if (exact) {
      onChange(exact.id);
      setQuery("");
    }
  };

  if (selected) {
    return (
      <div className="flex items-start justify-between gap-3 rounded-md border border-slate-300 px-3 py-2 text-sm dark:border-slate-700">
        <div className="min-w-0">
          <div className="font-medium">
            {selected.name}
            {selected.is_archived && <span className="ml-1 text-xs text-slate-500">(archived)</span>}
          </div>
          <div className="font-mono text-[11px] text-slate-500 break-all">{selected.id}</div>
        </div>
        {!disabled && (
          <button
            type="button"
            onClick={() => onChange("")}
            className="shrink-0 text-xs text-slate-500 hover:underline"
          >
            Change
          </button>
        )}
      </div>
    );
  }

  const q = query.trim();
  return (
    <div className="space-y-1">
      <input
        value={query}
        onChange={(e) => onQuery(e.target.value)}
        disabled={disabled}
        placeholder={placeholder ?? "Search by name or paste a Tempo deployment ID"}
        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm disabled:opacity-50 dark:bg-slate-800 dark:border-slate-700"
      />
      {!disabled && (
        <ul className="max-h-56 overflow-auto rounded-md border border-slate-200 text-sm dark:border-slate-700">
          {matches.length === 0 && (
            <li className="px-3 py-2 text-slate-400">
              No deployment matches{q ? ` “${q}”` : ""}.
            </li>
          )}
          {matches.slice(0, MAX_RESULTS).map((d) => (
            <li key={d.id}>
              <button
                type="button"
                onClick={() => {
                  onChange(d.id);
                  setQuery("");
                }}
                className="block w-full px-3 py-1.5 text-left hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <div>
                  {d.name}
                  <span className="text-slate-500">
                    {d.gpu_type_name ? ` · ${d.gpu_type_name}` : ""} · {d.num_nodes}
                    {d.gpus_per_node ? `×${d.gpus_per_node}` : " nodes"}
                    {d.is_archived ? " (archived)" : ""} · {d.assigned_device_count} assigned
                  </span>
                </div>
                <div className="font-mono text-[11px] text-slate-400">{d.id}</div>
              </button>
            </li>
          ))}
          {matches.length > MAX_RESULTS && (
            <li className="px-3 py-1.5 text-xs text-slate-400">
              {matches.length - MAX_RESULTS} more — keep typing to narrow down.
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
